<script setup>
// "Max's script" as a document (the pattern of Fin's Procedures, with no canvas): a header with the script's name,
// its state (Draft, with "unpublished changes" when there are) and the actions that come in the next step
// (Publish, History); the section "When it starts"; and the numbered steps, each one a node of the script's tree.
// Each step saves by itself. The store has the set; this component only reads it and saves a step.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { View16, ViewOff16 } from '@carbon/icons-vue';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import UpcomingButton from 'dashboard/components-next/polaris/cards/UpcomingButton.vue';
import { usePreviewPanel } from 'dashboard/components-next/polaris/preview/usePreviewPanel';
import ScenarioStepCard from './ScenarioStepCard.vue';

const props = defineProps({
  assistantId: { type: Number, required: true },
});
const emit = defineEmits(['update:dirty']);

const { t } = useI18n();
const store = useStore();
const { isOpen, setOpen } = usePreviewPanel();

const set = computed(() => store.getters['polarisScenarioSet/getSet']);
const nodes = computed(() => store.getters['polarisScenarioSet/getNodes']);
const catalog = computed(() => store.getters['polarisScenarioSet/getCatalog']);
const validation = computed(
  () => store.getters['polarisScenarioSet/getValidation']
);
const savingIds = computed(
  () => store.getters['polarisScenarioSet/getUIFlags'].savingNodeIds
);

// The name of the script: the one it carries, or its key said in words.
const name = computed(() => {
  const header = set.value?.header || {};
  const given = header.name || header.title;
  if (given) return given;
  const key = String(set.value?.scenario_key || '');
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
});

const entry = computed(() => {
  const key = set.value?.header?.entry;
  return nodes.value.find(node => node.node_key === key) || nodes.value[0];
});
const steps = computed(() =>
  nodes.value.map((node, index) => ({
    value: node.node_key,
    label: `${index + 1}. ${node.title || node.node_key}`,
  }))
);
const problems = computed(() => validation.value?.errors?.length || 0);

const opened = ref({});
const isStepOpen = (node, index) => opened.value[node.id] ?? index === 0;
const setStepOpen = (node, value) => {
  opened.value = { ...opened.value, [node.id]: value };
};

// Which steps have changes not saved yet: the page asks before leaving with any.
const dirtyIds = ref(new Set());
const onDirty = (id, value) => {
  const next = new Set(dirtyIds.value);
  next[value ? 'add' : 'delete'](id);
  dirtyIds.value = next;
  emit('update:dirty', next.size > 0);
};
const cards = ref({});
const discardAll = () =>
  Object.values(cards.value).forEach(card => card?.discard());

const save = async ({ id, title, node }) => {
  try {
    await store.dispatch('polarisScenarioSet/saveNode', {
      assistantId: props.assistantId,
      id,
      title,
      node,
    });
    useAlert(t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.API.SAVE_SUCCESS'));
  } catch (error) {
    if (error?.response?.status === 409) {
      useAlert(t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.API.CONFLICT'));
      discardAll();
      store
        .dispatch('polarisScenarioSet/get', props.assistantId)
        .catch(() => {});
      return;
    }
    useAlert(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.API.SAVE_ERROR')
    );
  }
};

const previewOpen = computed(() => isOpen(true));

defineExpose({ discardAll });
</script>

<template>
  <div class="grid gap-6" data-scenario-script>
    <header
      class="flex flex-wrap items-start justify-between gap-3"
      data-script-header
    >
      <div class="grid min-w-0 gap-1">
        <h2 class="truncate type-polaris-section-title" data-script-name>
          {{ name }}
        </h2>
        <div class="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" data-script-status>
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.STATUS.DRAFT') }}
          </Badge>
          <span
            v-if="set.dirty"
            class="type-polaris-caption text-polaris-muted-foreground"
            data-script-unpublished
          >
            {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.STATUS.UNPUBLISHED') }}
          </span>
          <span
            v-if="problems"
            class="type-polaris-caption text-polaris-destructive"
            data-script-problems
          >
            {{
              t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.STATUS.PROBLEMS', {
                count: problems,
              })
            }}
          </span>
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <UpcomingButton
          :label="t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.HISTORY')"
        >
          {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.HISTORY') }}
        </UpcomingButton>
        <UpcomingButton
          variant="default"
          :label="t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.PUBLISH')"
        >
          {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.PUBLISH') }}
        </UpcomingButton>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-script-toggle-preview
          @click="setOpen(!previewOpen)"
        >
          <component
            :is="previewOpen ? ViewOff16 : View16"
            aria-hidden="true"
          />
          {{
            t(
              previewOpen
                ? 'POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.HIDE_PREVIEW'
                : 'POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.SHOW_PREVIEW'
            )
          }}
        </Button>
      </div>
    </header>

    <section
      class="grid gap-2 rounded-polaris-xl border border-solid border-polaris-border bg-polaris-card p-4"
      data-script-when
    >
      <h3 class="type-polaris-card-title">
        {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.WHEN.TITLE') }}
      </h3>
      <p class="type-polaris-body text-polaris-muted-foreground">
        {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.WHEN.DESCRIPTION') }}
      </p>
      <p v-if="entry" class="type-polaris-body" data-script-entry>
        {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.WHEN.ENTRY') }}
        <strong>{{ entry.title || entry.node_key }}</strong>
      </p>
    </section>

    <div class="grid gap-3">
      <h3 class="type-polaris-card-title">
        {{ t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.STEPS.TITLE') }}
      </h3>
      <ScenarioStepCard
        v-for="(node, index) in nodes"
        :key="node.id"
        :ref="card => (cards[node.id] = card)"
        :open="isStepOpen(node, index)"
        :record="node"
        :index="index"
        :steps="steps"
        :catalog="catalog"
        :validation="validation"
        :saving="savingIds.includes(node.id)"
        :is-entry="entry?.id === node.id"
        @update:open="value => setStepOpen(node, value)"
        @dirty="onDirty"
        @save="save"
      />
    </div>
  </div>
</template>
